'use client';

import { useRef, useState } from 'react';
import { Button } from '@/components/sections/kit';
import { useFormGuard } from '@/components/forms/useFormGuard';
import { HUMAN_CHECK_FAILED, useHumanCheck } from '@/components/forms/useHumanCheck';
import { ABUSE_CATEGORIES, ABUSE_CATEGORY_LABELS } from '@/lib/report-abuse';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const inputCls =
  'w-full rounded-[11px] border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20';

/**
 * Abuse report form → POST /api/report-abuse (→ backend /v1/abuse-reports).
 * Same guard contract as ContactForm: honeypot + elapsed timing + a Turnstile
 * token on every submit (website#302).
 */
export default function ReportAbuseForm({ initialUrl = '' }: { initialUrl?: string }) {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const { guardFields, HoneypotField } = useFormGuard();
  const human = useHumanCheck();
  const formRef = useRef<HTMLFormElement>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting') return;

    const data = new FormData(e.currentTarget);
    const payload = {
      url: String(data.get('url') || '').trim(),
      category: String(data.get('category') || '').trim(),
      description: String(data.get('description') || '').trim() || undefined,
      reporter_email: String(data.get('reporter_email') || '').trim() || undefined,
      ...guardFields(),
    };

    setStatus('submitting');
    setError(null);
    try {
      const turnstileToken = await human.getToken();
      if (!turnstileToken) throw new Error(HUMAN_CHECK_FAILED);
      const res = await fetch('/api/report-abuse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, turnstileToken }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Something went wrong. Please try again.');
      setStatus('success');
      formRef.current?.reset();
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      human.reset();
    }
  }

  if (status === 'success') {
    return (
      <div className="rounded-2xl border border-green-300 bg-green-50 px-6 py-8 text-center">
        <h3 className="text-lg font-semibold text-green-900">Report received</h3>
        <p className="mt-2 text-sm text-green-800">
          Thank you. A person on the rii.link team will review it. If you left an email
          address, we may write to you about it.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-4 text-sm font-semibold text-green-900 underline underline-offset-2 hover:text-green-700"
        >
          Report another link
        </button>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} onFocus={human.arm} className="space-y-5" noValidate>
      <HoneypotField />
      <div>
        <label htmlFor="url" className="mb-1.5 block text-sm font-medium text-slate-700">
          Short link
        </label>
        <input
          id="url"
          name="url"
          type="text"
          inputMode="url"
          required
          maxLength={2048}
          autoComplete="off"
          defaultValue={initialUrl}
          className={inputCls}
          placeholder="https://rii.to/abc1234"
        />
      </div>

      <div>
        <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-slate-700">
          What is wrong with it?
        </label>
        <select id="category" name="category" required defaultValue="" className={inputCls}>
          <option value="" disabled>
            Choose a category
          </option>
          {ABUSE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {ABUSE_CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="description" className="mb-1.5 block text-sm font-medium text-slate-700">
          Details <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <textarea
          id="description"
          name="description"
          rows={5}
          maxLength={5000}
          className={`${inputCls} resize-y`}
          placeholder="Where did you find the link, and what does it lead to?"
        />
      </div>

      <div>
        <label htmlFor="reporter_email" className="mb-1.5 block text-sm font-medium text-slate-700">
          Your email <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <input
          id="reporter_email"
          name="reporter_email"
          type="email"
          autoComplete="email"
          maxLength={320}
          className={inputCls}
          placeholder="you@example.com"
        />
        <p className="mt-1.5 text-xs text-slate-500">Only if you want us to be able to reply.</p>
      </div>

      {status === 'error' && (
        <p role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </p>
      )}

      {human.widget}

      <Button
        type="submit"
        disabled={status === 'submitting'}
        className={`px-6 py-3.5 text-base ${status === 'submitting' ? 'opacity-60' : ''}`}
      >
        {status === 'submitting' ? 'Sending…' : 'Send report'}
      </Button>

      <p className="text-xs leading-[1.55] text-slate-500">
        What you send here is used only to review the reported link.
      </p>
    </form>
  );
}
